import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { weighingPhotoKeys } from "../query-keys";
import type { BuildPhoto } from "./use-build-photos";
import type { PhotoId, PlatformWeighingId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

/** Same branded row as every other photo surface. */
export type WeighingPhoto = BuildPhoto;

export function useWeighingPhotos(weighingId: PlatformWeighingId) {
  return useQuery({
    queryKey: weighingPhotoKeys.byWeighing(weighingId),
    queryFn: async () => {
      const result = await api.GET("/api/platform-weighings/{weighing_id}/photos", {
        params: { path: { weighing_id: weighingId } },
      });
      return throwIfError(result, "Failed to fetch weighing photos") as WeighingPhoto[];
    },
  });
}

export function useDeleteWeighingPhoto(weighingId: PlatformWeighingId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (photoId: PhotoId) => {
      const result = await api.DELETE("/api/platform-weighings/{weighing_id}/photos/{photo_id}", {
        params: { path: { weighing_id: weighingId, photo_id: photoId } },
      });
      throwIfError(result, "Failed to delete photo");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: weighingPhotoKeys.byWeighing(weighingId) });
    },
  });
}

/** Merge a background-upload result into the weighing-photos cache — a
 *  single-category retake replaces the category's photo in place. */
export function mergeUploadedWeighingPhoto(
  qc: QueryClient,
  weighingId: PlatformWeighingId,
  photo: components["schemas"]["PhotoResponse"],
  allowMultiple: boolean,
) {
  const incoming = photo as WeighingPhoto;
  qc.setQueryData<WeighingPhoto[]>(weighingPhotoKeys.byWeighing(weighingId), (prev) => {
    if (!prev) return [incoming];
    const rest = allowMultiple
      ? prev.filter((p) => p.id !== incoming.id)
      : prev.filter((p) => p.category_id !== incoming.category_id);
    return [...rest, incoming];
  });
}
